import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Briefcase, Filter, Search, MapPin, Building2, DollarSign, ExternalLink } from 'lucide-react';
import { LoadingSpinner, EmptyState } from '../UI/Card';
import type { Job, JobType, JobCategory } from '../../types/database';

const JOB_TYPES: { value: JobType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'volunteer', label: 'Volunteer' },
];

const CATEGORIES: { value: JobCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'tech', label: 'Technology' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'retail', label: 'Retail' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' },
];

export function Jobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<JobType | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<JobCategory | 'all'>('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          employer:profiles(full_name, organization)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || job.job_type === selectedType;
    const matchesCategory = selectedCategory === 'all' || job.category === selectedCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen">
      <div className="glass-card bg-gradient-to-r from-indigo-500 to-blue-500 text-white py-12 mx-4 mt-4 mb-6 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Briefcase className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">Job Opportunities</h1>
          </div>
          <p className="text-indigo-100 text-lg">
            Find your next opportunity in the local community
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="glass-card p-6 mb-8">
          <div className="flex flex-col space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search jobs by title, company, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="glass-input w-full pl-10"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-2 flex-1">
                <Filter className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as JobType | 'all')}
                  className="glass-input flex-1"
                >
                  {JOB_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as JobCategory | 'all')}
                  className="glass-input w-full"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {filteredJobs.length === 0 ? (
          <EmptyState
            icon={<Briefcase className="w-full h-full" />}
            title="No jobs found"
            description="Try adjusting your search or filters"
          />
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="glass-card p-6 group"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <Building2 className="w-4 h-4 mr-2" />
                            <span className="font-medium">{job.company_name}</span>
                          </div>
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <MapPin className="w-4 h-4 mr-2" />
                            <span>{job.location}</span>
                          </div>
                          {job.salary_range && (
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <DollarSign className="w-4 h-4 mr-2" />
                              <span>{job.salary_range}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-2">
                          {job.description}
                        </p>
                        {job.requirements && (
                          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 mb-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Requirements:</h4>
                            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{job.requirements}</p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 rounded-full text-sm font-medium capitalize">
                            {job.job_type}
                          </span>
                          <span className="px-3 py-1 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium capitalize">
                            {job.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lg:ml-6">
                    {job.application_url ? (
                      <a
                        href={job.application_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="glass-button inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:from-indigo-700 hover:to-blue-600 transition-all duration-300 w-full lg:w-auto"
                      >
                        <span>Apply Now</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <button className="glass-button inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium w-full lg:w-auto">
                        Contact Employer
                      </button>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                      Posted {new Date(job.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
